import React, { useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Navigate } from "react-router-dom";
import { Spin } from "antd";
import {
  fetchUserDataAction,
  fetchClientCountries,
} from "../slices/authSlice";
// Reuse the main app's store-independent session hooks (admin → web imports are
// the sanctioned direction). The idle callback dispatches admin's OWN logout.
import {
  useIdleTimer,
  isSessionStale,
  clearActivityTimestamp,
} from "@web/hooks/useIdleTimer";
import { performLogout } from "../utils/performLogout";
import StaffAccountGate from "./StaffAccountGate";

const ProtectedRoute = ({ children }) => {
  const dispatch = useDispatch();
  const { isInitialized, isAuthenticated, user, accountStatus } = useSelector(
    (state) => state.auth
  );
  const uid = user?.uid;

  // Session-age check: runs synchronously on first render BEFORE any effects,
  // so it reflects the age of the RESTORED session rather than a freshly
  // written activity timestamp. Mirrors apps/web's ProtectedRoute.
  const sessionStaleRef = useRef(null);
  if (sessionStaleRef.current === null) {
    sessionStaleRef.current = isAuthenticated && isSessionStale();
    if (sessionStaleRef.current) {
      clearActivityTimestamp();
    }
  }

  // Stale session on load → hard-logout to /login with the inactivity banner.
  useEffect(() => {
    if (sessionStaleRef.current) {
      performLogout(dispatch, "session_expired");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Idle timer: log out after 1 hour of inactivity, matching the main app.
  const handleIdle = useCallback(() => {
    clearActivityTimestamp();
    performLogout(dispatch, "session_expired");
  }, [dispatch]);
  useIdleTimer(handleIdle, 3600000);

  useEffect(() => {
    if (!isAuthenticated || !uid || accountStatus !== "ready") return;
    let cancelled = false;

    (async () => {
      try {
        if (import.meta.env.DEV) console.log("ProtectedRoute: Fetching user data for user:", uid);
        const result = await dispatch(
          fetchUserDataAction({ withScope: true })
        ).unwrap();

        if (!cancelled) {
          if (import.meta.env.DEV) {
            console.log(
              "ProtectedRoute: User data fetched successfully:",
              result
            );
          }
          // Fetch countries for the active workspace (needed by CASS and other pages)
          dispatch(fetchClientCountries({ product_name: "execution_view" }));
        }
      } catch (err) {
        if (!cancelled) {
          console.error("ProtectedRoute: fetch user failed", err);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dispatch, isAuthenticated, uid, accountStatus]);
  if (sessionStaleRef.current) {
    return <div role="status">Signing out expired session…</div>;
  }
  // Wait for Firebase/Redux to restore auth state before making routing decisions
  if (!isInitialized) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to="/login"
        replace
      />
    );
  }
  return <StaffAccountGate>{children}</StaffAccountGate>;
};

export default ProtectedRoute;
